import { useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { EmployeeSoftwareDetailsPageQueryDocument } from 'generated/resources/graphql'
import moment from 'moment'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import InstalledSoftwareList from 'modules/software_management/components/softwareByEmployee/InstalledSoftwareList'
import OtherSoftwareList from 'modules/software_management/components/softwareByEmployee/OtherSoftwareList'
import { useCreateEmployeeSoftwareMembership } from 'modules/software_management/hooks/useCreateEmployeeSoftwareMembership'
import { useDeleteEmployeeSoftwareMembership } from 'modules/software_management/hooks/useDeleteEmployeeSoftwareMembership'

import { UnionSoftwareType, getSoftware } from '../lib/software'
import { useApplicationsContext } from './ApplicationsPage'

const EmployeeSoftwareDetailsPage = () => {
  const { employeeId } = useParams()
  const { currency } = useApplicationsContext()
  const companyId = useCurrentSession().company.id
  const beginOfYear = moment().startOf('year').format('YYYY-MM-DD')
  const endOfYear = moment().endOf('year').format('YYYY-MM-DD')
  const {
    access: { locale },
  } = useCurrentSession()

  const { data, loading, error } = useQuery(EmployeeSoftwareDetailsPageQueryDocument, {
    variables: {
      companyId: companyId,
      employeeIds: [Number(employeeId)],
      dateFrom: beginOfYear,
      dateTo: endOfYear,
      currency,
    },
  })

  const employeeSoftwareMemberships = data?.softwareManagement.employeeSoftwareMemberships || []
  const softwareSetups = data?.softwareManagement.softwareSetups || []

  const installedSoftwares = employeeSoftwareMemberships.map((esm) => {
    const software = getSoftware(esm)
    return software.id
  })

  const otherSoftware = softwareSetups.filter((softwareSetup) => {
    const software = getSoftware(softwareSetup)
    return !installedSoftwares.includes(software.id)
  })

  const employee = data?.employees.employeesConnection.nodes[0]

  const { delete: deleteEmployeeSoftwareMembership, loading: loadingDeleteSoftwareMembership } =
    useDeleteEmployeeSoftwareMembership()

  const { performAction: removeSoftware } = useAsyncAction(
    async (membershipId: number) => {
      const result = await deleteEmployeeSoftwareMembership({
        id: membershipId,
        employeeId: Number(employeeId),
        companyId: companyId,
        dateFrom: beginOfYear,
        dateTo: endOfYear,
        currency,
      })

      const handled = handleResult(result, () => undefined)

      if (handled.success) {
        const employeeSoftwareMembership = handled.data.employeeSoftwareMembership!
        const { id: software_id, name: software_name } =
          employeeSoftwareMembership.software || employeeSoftwareMembership.customSoftware!

        track(trackingEvents.softwareManagement.removeSoftwareFromEmployee, {
          employee_id: employeeId,
          software_id,
          software_name,
        })
      }
    },
    {
      successFlash: true,
      errorFlash: true,
      successMessage: i18n.t(
        'software_management.employee_software.detail.alert.uninstall_success'
      ),
      errorMessage: i18n.t('software_management.employee_software.detail.alert.uninstall_error'),
    }
  )

  const { create: createEmployeeSoftwareMembership } = useCreateEmployeeSoftwareMembership()

  const { performAction: createEmployeeMembership } = useAsyncAction(
    async (employeeId: number, software: UnionSoftwareType) => {
      const res = await createEmployeeSoftwareMembership({
        employeeId,
        softwareId: software.id,
        companyId: companyId,
        dateFrom: beginOfYear,
        dateTo: endOfYear,
        currency,
      })
      if (!res.errors) {
        const { id: software_id, name: software_name } = software
        track(trackingEvents.softwareManagement.addSoftwareToEmployee, {
          employee_id: employeeId,
          software_id,
          software_name,
        })
      }
    },
    {
      successFlash: true,
      errorFlash: true,
      successMessage: i18n.t('software_management.employee_software.detail.alert.install_success'),
      errorMessage: i18n.t('software_management.employee_software.detail.alert.install_error'),
    }
  )

  useEffect(() => {
    track(trackingEvents.softwareManagement.goToEmployeeDetail, {
      employee_id: employeeId,
    })
  }, [employeeId])

  const softwareIds: string[] = employeeSoftwareMemberships.map(
    (membership) => getSoftware(membership).id
  )
  const month = moment().startOf('month').format('M')
  const cost = softwareSetups
    .filter(
      (softwareSetup) =>
        softwareSetup.software?.id && softwareIds.includes(softwareSetup.software?.id)
    )
    .reduce((acc, softwareSetup) => {
      const monthlyCost = softwareSetup.monthlySoftwareCosts.find(
        (c) => moment(c.monthlyIndexedDate).month() + 1 === parseInt(month)
      ) || { costInCents: 0 }
      acc = acc + monthlyCost.costInCents

      return acc
    }, 0)

  const location = data?.locations.employeesLocationsConnection.nodes.find(
    (node) => node.location?.id === employee?.locationId
  )?.location

  return (
    <>
      <Box style={{ flexGrow: '0' }}>
        <ResourceHeader
          avatar={{
            type: 'person',
            firstName: employee?.firstName || '',
            lastName: employee?.lastName || '',
            src: buildUrl(employee?.access?.avatar),
          }}
          title={employee?.fullName ?? ''}
          description={employee?.referenceContract?.jobTitle || ''}
          status={
            !employee?.terminatedOn
              ? {
                  label: i18n.t('software_management.employee_software.detail.header.active'),
                  text: i18n.t('software_management.employee_software.detail.header.active'),
                  variant: 'positive',
                }
              : {
                  label: i18n.t('software_management.employee_software.detail.header.terminated'),
                  text: i18n.t('software_management.employee_software.detail.header.terminated'),
                  variant: 'critical',
                }
          }
          metadata={[
            {
              label: i18n.t('software_management.employee_software.detail.header.software_cost'),
              value: {
                type: 'text',
                content: MoneyWithCurrency({
                  amount: cost,
                  currency,
                  locale,
                }),
              },
            },
            employee?.memberships[0]?.team?.name
              ? {
                  label: i18n.t('software_management.employee_software.detail.header.team'),
                  value: {
                    text: employee.memberships[0].team.name,
                    type: 'avatar',
                    variant: {
                      name: employee.memberships[0].team.name,
                      type: 'team',
                    },
                  },
                }
              : undefined,
            location
              ? {
                  label: i18n.t('software_management.employee_software.detail.header.location'),
                  value: {
                    content: location.name,
                    type: 'text',
                  },
                }
              : undefined,
          ]}
        />
      </Box>
      <ResponsivePage.Body variant="wide" loading={loading} error={error} showHeader={false}>
        <Box gap="s48" paddingTop="s16">
          <InstalledSoftwareList
            employeeSoftwareMemberships={employeeSoftwareMemberships}
            softwareSetups={softwareSetups}
            removeSoftware={removeSoftware}
            loadingDeleteSoftwareMembership={loadingDeleteSoftwareMembership}
            currency={currency}
          />
          {employeeId && softwareSetups && (
            <OtherSoftwareList
              employeeId={employeeId}
              softwareSetups={otherSoftware}
              createEmployeeMembership={createEmployeeMembership}
              loadingSoftwareSetups={loading}
              currency={currency}
            />
          )}
        </Box>
      </ResponsivePage.Body>
    </>
  )
}

export default EmployeeSoftwareDetailsPage
